// FinancialPositionCard — the summary's money section: what the contract is
// worth, what has been billed against it, what has been approved, and what is
// still committed on purchase orders.
//
// Two vocabulary rules run through this card and are asserted in its tests:
//
//   1. "Approved", never "Paid". Invoice statuses stop at approved; the system
//      holds no settlement record, so a "Paid" label would be a claim the data
//      cannot support.
//   2. The invoice-consumption position (approved / awaiting approval /
//      remaining) and the purchase-order commitment rollup are DIFFERENT
//      bases and are labelled as such. Reading a fully committed PO as a fully
//      consumed contract is the specific mistake the separation prevents.
//
// The contract value renders here at full precision — the band carries the
// abbreviated one, and those two are the only places it appears.
//
// The consumption position itself is drawn by the shared
// `CumulativePositionBar`, which the financial performance report also uses,
// so the summary and the report cannot draw the same position differently.

import MetricCard from "../tracking/deliverable/MetricCard";
import StackedBarBreakdown from "../tracking/shared/StackedBarBreakdown";
import CumulativePositionBar from "./CumulativePositionBar";
import ReportSection, { UnavailableNotice } from "./ReportSection";
import { PurchaseOrderStatusBadge } from "./reportStatusBadges";
import { countTone } from "./reportFormat";
import { formatAud2dp, formatAudCompact } from "../../../utils/formatCurrency";
import type {
  SectionUnavailable,
  SummaryFinancialSection,
} from "../../../types/report";

const SECTION_LABEL = "Financial position";

export interface FinancialPositionCardProps {
  section: SummaryFinancialSection | SectionUnavailable;
}

export default function FinancialPositionCard({
  section,
}: FinancialPositionCardProps) {
  if (section.status === "unavailable") {
    return (
      <ReportSection id="financial" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }

  const { cumulative, valueBreakdown, contractRollup, purchaseOrders } = section;

  return (
    <ReportSection id="financial" title={SECTION_LABEL}>
      <p
        data-testid="financial-gst-basis"
        className="text-caption text-slate-500 dark:text-slate-400"
      >
        All values {section.gstBasis}.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          testId="financial-contract-value"
          label="Total contract value"
          value={formatAud2dp(section.contractValue)}
          ariaLabel={`Total contract value ${formatAud2dp(section.contractValue)} ${section.gstBasis}, made up of contract ${formatAud2dp(valueBreakdown.head)}, statements of work ${formatAud2dp(valueBreakdown.sows)}, and variations ${formatAud2dp(valueBreakdown.variations)}`}
          breakdown={
            <StackedBarBreakdown
              testId="financial-value-breakdown"
              total={section.contractValue}
              segments={[
                {
                  key: "head",
                  label: "Contract",
                  value: valueBreakdown.head,
                  colorClass: "bg-brand-500 dark:bg-brand-400",
                  formattedValue: formatAudCompact(valueBreakdown.head),
                },
                {
                  key: "sows",
                  label: "SOWs",
                  value: valueBreakdown.sows,
                  colorClass: "bg-emerald-500 dark:bg-emerald-400",
                  formattedValue: formatAudCompact(valueBreakdown.sows),
                },
                {
                  key: "variations",
                  label: "Variations",
                  value: valueBreakdown.variations,
                  colorClass: "bg-amber-500 dark:bg-amber-400",
                  formattedValue: formatAudCompact(valueBreakdown.variations),
                },
              ]}
            />
          }
        />
        <MetricCard
          testId="financial-invoiced"
          label="Invoiced to date"
          value={formatAud2dp(section.invoicedToDate)}
        />
        <MetricCard
          testId="financial-approved"
          label="Approved to date"
          value={formatAud2dp(section.approvedToDate)}
        />
        <MetricCard
          testId="financial-in-review"
          label="Invoices awaiting review"
          value={section.inReviewCount}
          valueTone={countTone(section.inReviewCount, "warning")}
          subtitle={formatAud2dp(section.inReviewValue)}
        />
      </div>

      <div className="mt-5">
        <p className="text-caption font-medium text-slate-500 dark:text-slate-400">
          Contract value consumed by invoices
        </p>
        <div className="mt-1">
          <CumulativePositionBar
            cumulative={cumulative}
            gstBasis={section.gstBasis}
            barTestId="financial-consumption"
            paceTestId="financial-pace"
            overrunTestId="financial-overrun"
          />
        </div>
      </div>

      <div className="mt-5">
        <p className="text-caption font-medium text-slate-500 dark:text-slate-400">
          Purchase orders
        </p>
        <p
          data-testid="financial-po-basis-note"
          className="mt-0.5 text-caption text-slate-500 dark:text-slate-400"
        >
          Purchase-order commitment against contract value — a different basis
          from the invoice consumption above. Committed{" "}
          {formatAud2dp(contractRollup.committed)} of{" "}
          {formatAud2dp(contractRollup.contractValue)}.
        </p>
        {purchaseOrders.length === 0 ? (
          <p className="mt-2 text-body-sm text-slate-500 dark:text-slate-400">
            No purchase orders recorded
          </p>
        ) : (
          <div
            className="mt-2 overflow-x-auto"
            tabIndex={0}
            role="group"
            aria-label="Purchase order position against each order's approved limit"
          >
            <table className="w-full">
              <caption className="sr-only">
                Purchase order position against each order&apos;s approved
                limit
              </caption>
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Purchase order
                  </th>
                  <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                    Status
                  </th>
                  <th className="px-3 py-2 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                    Limit
                  </th>
                  <th className="px-3 py-2 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                    Approved
                  </th>
                  <th className="px-3 py-2 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                    Awaiting review
                  </th>
                  <th className="px-3 py-2 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                    Remaining
                  </th>
                </tr>
              </thead>
              <tbody>
                {purchaseOrders.map((po) => (
                  <tr
                    key={po.id}
                    className="border-b border-slate-100 dark:border-slate-700"
                  >
                    <td className="px-3 py-2 text-body-sm text-slate-900 dark:text-slate-100">
                      <span className="font-mono">{po.poNumber}</span>
                      {po.description && (
                        <span className="block text-caption text-slate-500 dark:text-slate-400">
                          {po.description}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <PurchaseOrderStatusBadge status={po.status} />
                    </td>
                    <td className="px-3 py-2 text-right text-body-sm tabular-nums text-slate-900 dark:text-slate-100">
                      {formatAud2dp(po.position.limit)}
                    </td>
                    <td className="px-3 py-2 text-right text-body-sm tabular-nums text-slate-900 dark:text-slate-100">
                      {formatAud2dp(po.position.approved)}
                    </td>
                    <td className="px-3 py-2 text-right text-body-sm tabular-nums text-slate-900 dark:text-slate-100">
                      {formatAud2dp(po.position.inReview)}
                    </td>
                    <td className="px-3 py-2 text-right text-body-sm tabular-nums text-slate-900 dark:text-slate-100">
                      {formatAud2dp(po.position.remaining)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ReportSection>
  );
}
